import {
  Alert,
  Button,
  Card,
  DatePicker,
  Form,
  List,
  Table,
  Tag,
} from 'antd';
import moment from 'moment';

import apiCompaniesCall from 'apps/futureflex/apiCalls/company/list';

import { useQueryClient } from '@tanstack/react-query';
import { useFetchApiList, usePostApi } from 'hooks/api';
import MetaDecorator from 'apps/home/components/MetaDecorator';
import PageTitle from 'apps/home/components/PageTitle';
import {
  orderSideColors,
  orderSideLabels,
  programOrderStatus,
  programOrderStatusColors,
} from 'apps/investment/constants/programOrder';
import {
  orderTypeColors,
  orderTypeLabels,
} from 'apps/investment/constants/fundOrder';
import SelectListData from 'common/form/SelectListData';
import { formatTableColumns } from 'common/ui/DataTable/config';
import LoadingSpinner from 'common/ui/LoadingSpinner';
import QueryString from 'qs';
import { useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { formatDateTime } from 'utils/formatDate';

const title = 'Bundle Flex Orders';

const bundlePreviewColumns = [
  {
    title: 'ID',
    dataIndex: 'code',
    render: (value, record) => (
      <Link to={`/futureflex/program-orders/${record.id}`}>{value}</Link>
    ),
  },
  {
    title: 'Order Side',
    dataIndex: 'order_side',
    render: text => (
      <Tag color={orderSideColors[text]}>{orderSideLabels[text]}</Tag>
    ),
  },
  {
    title: 'Employee',
    dataIndex: 'employee',
    render: value => value?.full_name,
  },
  {
    title: 'Interval',
    dataIndex: 'interval',
    align: 'right',
  },
  {
    title: 'Order Type',
    dataIndex: 'order_type',
    align: 'center',
    render: (text, record) => (
      <Tag color={orderTypeColors[record.order_type]}>
        {orderTypeLabels[record.order_type] || record.order_type}
      </Tag>
    ),
  },
  {
    title: 'Amount',
    dataIndex: 'amount',
    type: 'formatNumber',
  },
  {
    title: 'Product Code',
    dataIndex: 'product_code',
    align: 'center',
  },
  {
    title: 'Created',
    dataIndex: 'created_at',
    render: value => formatDateTime(value),
  },
  {
    title: 'Status',
    dataIndex: 'status',
    align: 'center',
    render: status => (
      <Tag color={programOrderStatusColors[status]}>
        {programOrderStatus[status]}
      </Tag>
    ),
  },
  {
    title: 'Fund Order',
    dataIndex: 'order',
    render: order => {
      if (!order) return '-';
      return (
        <Link to={`/investment/fund-orders/${order.id}`}>{order.code}</Link>
      );
    },
  },
];

function parseSearchParams(searchParams) {
  const parsed = QueryString.parse(searchParams.toString());
  return {
    company_id: parsed.company_id ? Number(parsed.company_id) : undefined,
    month: parsed.month ? moment(parsed.month, 'YYYY-MM') : undefined,
  };
}

const BundleForm = ({ apiCall }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialValues = parseSearchParams(searchParams);
  const [form] = Form.useForm();
  const queryClient = useQueryClient();

  const { post: submitItem, isLoading: submiting } = usePostApi(
    apiCall.bundle,
    {
      resourceName: 'data',
      // Bundling commits the merged program/fund orders in a transaction
      // (BundleProgramBuyOrders) before returning, so the list below shows
      // the pre-bundle rows until it is refetched.
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [apiCall.list.queryKey] });
      },
    }
  );

  const formValues = Form.useWatch([], form) || {};
  const company_id = formValues.company_id;
  const month = formValues.month;
  const monthStr = month ? moment(month).format('YYYY-MM') : undefined;

  const {
    data: programOrders = [],
    load,
    loadMore,
    isLoading,
    isLoadingMore,
    paging,
  } = useFetchApiList(apiCall.list, { resourceName: 'data' });

  // Sync form values to URL search params
  useEffect(() => {
    const params = {};
    if (company_id) params.company_id = company_id;
    if (monthStr) params.month = monthStr;
    setSearchParams(params, { replace: true });
  }, [company_id, monthStr]);

  // Fetch orders for the selected company
  useEffect(() => {
    if (company_id) {
      load({ company_id });
    }
  }, [company_id]);

  const onFinish = () => {
    if (!company_id || !monthStr) return;
    if (
      confirm(
        'Are you sure you want to bundle flex orders for this company?'
      )
    ) {
      submitItem({ payload: { company_id, month: monthStr } });
    }
  };

  const { page, count, page_size } = paging || {};

  return (
    <div className="flex flex-col gap-6">
      <MetaDecorator title={title} />
      <PageTitle title={title} backUrl={`/futureflex/program-orders`} />

      <Alert
        type="info"
        description={
          <List
            size="small"
            dataSource={[
              '"Bundle" là nghiệp vụ đóng gói các PO (Program Orders) thành FO (Fund Orders) để gửi đến quỹ.',
              'Chọn Doanh nghiệp và tháng tương ứng, hệ thống sẽ hiển thị danh sách các PO trong tháng đó.',
              'Nhấn "Bundle" để hệ thống tự động đóng gói các PO có trạng thái "Mới" thành các FO.',
            ]}
            renderItem={item => <List.Item>{item}</List.Item>}
          />
        }
      />

      <Card className="shadow-sm">
        <Form
          form={form}
          name={'bundle_form'}
          onFinish={onFinish}
          layout="vertical"
          scrollToFirstError
          initialValues={initialValues}
        >
          <div className="flex gap-4 items-end">
            <Form.Item
              name="company_id"
              label="Company"
              className="flex-1 mb-0"
              rules={[{ required: true, message: 'Please select a company' }]}
            >
              <SelectListData
                apiCall={apiCompaniesCall}
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option?.label ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                placeholder="Search Email / Full company name"
                callback={({ id, email, name }) => ({
                  value: id,
                  label: name + ' / ' + email,
                })}
              />
            </Form.Item>

            <Form.Item
              name="month"
              label="Month"
              className="w-40 mb-0"
              rules={[{ required: true, message: 'Please select a month' }]}
            >
              <DatePicker picker="month" format="YYYY-MM" className="w-full" />
            </Form.Item>

            <Form.Item className="mb-0">
              <Button type="primary" htmlType="submit" loading={submiting}>
                Bundle
              </Button>
            </Form.Item>
          </div>
        </Form>
      </Card>

      {!!company_id && (
        <Card className="shadow-sm" title="Program Orders">
          {isLoading ? (
            <LoadingSpinner />
          ) : (
            <Table
              rowKey={e => e?.id}
              size="small"
              dataSource={programOrders}
              loading={isLoadingMore}
              columns={formatTableColumns(bundlePreviewColumns)}
              pagination={{
                current: page,
                total: count,
                hideOnSinglePage: true,
                pageSize: page_size,
                showSizeChanger: false,
                onChange: p => {
                  loadMore({ company_id, page: p });
                },
              }}
            />
          )}
        </Card>
      )}
    </div>
  );
};

export default BundleForm;
